import { useState } from "react";
import {
  Check,
  Package,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  X,
  Sparkles,
  AlertTriangle,
  Home as HomeIcon,
} from "lucide-react";

/**
 * Homethyst Design System — "Warm Order" (v2 prototype)
 *
 * A working, self-contained preview of the replacement design direction described in
 * the repository-root DESIGN.md. Tokens are declared as scoped CSS custom properties on
 * the page root so this renders faithfully regardless of the global Tailwind @theme,
 * which still ships the previous "Sage Garden" tokens. This page does not modify the
 * live app theme.
 *
 * North star: amethyst is the ink and the signal, warm paper is the ground,
 * and sage is both the visible payoff for order and the assistive (AI) voice.
 * Two calm sans voices: Hanken Grotesk (display) + Inter (UI/body). No serif; no second purple.
 */

// --- Token tables (mirror DESIGN.md frontmatter) --------------------------------

const TOKENS = {
  // amethyst — ink & signal
  amethystInk: "#3E3251",
  amethyst: "#5A4A78",
  amethystSoft: "#EDE7F2",
  // sage — the visible payoff
  sage: "#6E8C6A",
  sageDeep: "#4C6B4A",
  sageDeepHover: "#3D5A3B",
  sageWash: "#E7EEE4",
  // warm ground
  paper: "#F6F2EC",
  paperRaised: "#FBF9F5",
  card: "#FFFFFF",
  clayBorder: "#E4DCD0",
  clayMuted: "#EAE3D8",
  // text
  ink: "#2A2530",
  body: "#524B57",
  faint: "#736C79",
  // status
  success: "#12874A",
  successWash: "#E4EFE7",
  successText: "#0B5B33",
  error: "#C0562F",
  errorWash: "#F7E9E2",
  errorText: "#8F3E1F",
  warning: "#C9862E",
  warningWash: "#F6ECD9",
  warningText: "#7A5010",
};

// Display voice: Hanken Grotesk — a calm humanist sans (not a serif, not a geometric
// "template" sans). Inter carries all functional UI and body.
const DISPLAY = "'Hanken Grotesk', system-ui, sans-serif";
const SANS = "'Inter', system-ui, sans-serif";

const COLOR_GROUPS: {
  title: string;
  note: string;
  swatches: { name: string; hex: string; text?: string; desc: string }[];
}[] = [
  {
    title: "Amethyst — ink & signal",
    note: "Used with authority and rarity. Never a page background.",
    swatches: [
      { name: "Amethyst Ink", hex: "#3E3251", text: "#FFFFFF", desc: "Headings, logo, high-emphasis accents" },
      { name: "Amethyst", hex: "#5A4A78", text: "#FFFFFF", desc: "Interactive fill — buttons, active nav, links" },
      { name: "Amethyst Soft", hex: "#EDE7F2", text: "#3E3251", desc: "Selected state + tint wash (designed token)" },
    ],
  },
  {
    title: "Sage — calm, order & assistance",
    note: "Strong enough to see. Marks 'in order / at rest' AND carries AI / assistive actions.",
    swatches: [
      { name: "Sage", hex: "#6E8C6A", text: "#FFFFFF", desc: "Glyphs, tints, gradient endpoint (non-text)" },
      { name: "Sage Deep", hex: "#4C6B4A", text: "#FFFFFF", desc: "Assistive button fills + sage words (AA)" },
      { name: "Sage Wash", hex: "#E7EEE4", text: "#524B57", desc: "At-rest / assistive backgrounds under text" },
    ],
  },
  {
    title: "Warm neutrals — ground & structure",
    note: "The page is paper; white is the working surface.",
    swatches: [
      { name: "Paper", hex: "#F6F2EC", text: "#524B57", desc: "Default page background" },
      { name: "Paper Raised", hex: "#FBF9F5", text: "#524B57", desc: "Secondary raised surface" },
      { name: "Card", hex: "#FFFFFF", text: "#524B57", desc: "Crisp working surface" },
      { name: "Clay Border", hex: "#E4DCD0", text: "#524B57", desc: "Warm 1px hairline / divider" },
      { name: "Clay Muted", hex: "#EAE3D8", text: "#524B57", desc: "Inactive pills, toggle troughs" },
    ],
  },
  {
    title: "Status — domestic & warm",
    note: "Not Bootstrap red/amber/green. Each has a vivid tone + a verified dark-text tone.",
    swatches: [
      { name: "Success", hex: "#12874A", text: "#FFFFFF", desc: "Vivid emerald — clearly ≠ resting sage" },
      { name: "Error", hex: "#C0562F", text: "#FFFFFF", desc: "Terracotta — validation, destructive confirm" },
      { name: "Warning", hex: "#C9862E", text: "#FFFFFF", desc: "Amber-ochre — non-blocking cautions" },
    ],
  },
];

const TYPE_SCALE = [
  { label: "Display / Page Title", sample: "A calm home, in order", font: DISPLAY, size: "2.5rem", weight: 600, ls: "-0.02em", lh: 1.08, meta: "Hanken Grotesk 600 · 40px" },
  { label: "Headline", sample: "Everything in its place", font: DISPLAY, size: "1.625rem", weight: 600, ls: "-0.01em", lh: 1.2, meta: "Hanken Grotesk 600 · 26px" },
  { label: "Title", sample: "Kitchen — 24 items", font: SANS, size: "1.0625rem", weight: 600, lh: 1.35, meta: "Inter 600 · 17px" },
  { label: "Body", sample: "Track what you own, where it is, and what needs attention.", font: SANS, size: "0.9375rem", weight: 400, lh: 1.55, meta: "Inter 400 · 15px" },
  { label: "Label", sample: "Space name", font: SANS, size: "0.875rem", weight: 500, lh: 1.5, meta: "Inter 500 · 14px" },
  { label: "Caption", sample: "Updated 2 hours ago · 3 items", font: SANS, size: "0.8125rem", weight: 400, lh: 1.45, meta: "Inter 400 · 13px", faint: true },
];

const RADII = [
  { label: "sm · 6px", note: "inputs", r: 6 },
  { label: "md · 10px", note: "buttons, nav", r: 10 },
  { label: "lg · 14px", note: "cards", r: 14 },
  { label: "pill", note: "tags, progress", r: 9999 },
];

const SPACING = [
  { name: "xs", val: 4 },
  { name: "sm", val: 8 },
  { name: "md", val: 16 },
  { name: "lg", val: 20 },
  { name: "xl", val: 24 },
  { name: "2xl", val: 32 },
  { name: "3xl", val: 48 },
];

// --- Small building blocks ------------------------------------------------------

function SectionHeading({ children, kicker }: { children: React.ReactNode; kicker: string }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontFamily: DISPLAY, fontSize: "1.625rem", fontWeight: 600, lineHeight: 1.15, color: TOKENS.ink, margin: 0 }}>
        {children}
      </h2>
      <p style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, margin: "6px 0 0" }}>{kicker}</p>
    </div>
  );
}

function Card({
  children,
  style,
  interactive,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  interactive?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => interactive && setHover(false)}
      style={{
        background: TOKENS.card,
        border: `1px solid ${hover ? TOKENS.amethyst + "66" : TOKENS.clayBorder}`,
        borderRadius: 14,
        padding: 20,
        transition: "border-color 160ms ease",
        cursor: interactive ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Btn({
  children,
  variant = "primary",
  icon,
  disabled,
}: {
  children: React.ReactNode;
  variant?: "primary" | "assistive" | "secondary" | "ghost" | "destructive";
  icon?: React.ReactNode;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const base: React.CSSProperties = {
    fontFamily: SANS,
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: 10,
    padding: "9px 16px",
    border: "1px solid transparent",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 140ms ease, border-color 140ms ease",
  };
  // Disabled is its own flat treatment — a greyed, borderless-color inert control — not a
  // faded copy of the enabled fill. It reads unmistakably as "not available" while keeping
  // its label AA-legible (body on clay-muted = 6.6:1), unlike a 40%-opacity dim.
  const disabledStyle: React.CSSProperties = {
    background: TOKENS.clayMuted,
    color: TOKENS.body,
    fontWeight: 400,
    border: `1px solid ${TOKENS.clayBorder}`,
  };
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: hover ? TOKENS.amethystInk : TOKENS.amethyst, color: "#fff" },
    assistive: { background: hover ? TOKENS.sageDeepHover : TOKENS.sageDeep, color: "#fff" },
    secondary: { background: TOKENS.amethystSoft, color: TOKENS.amethystInk },
    ghost: { background: hover ? TOKENS.amethystSoft : "transparent", color: TOKENS.amethyst },
    destructive: { background: TOKENS.errorWash, color: TOKENS.errorText },
  };
  return (
    <button
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...base, ...(disabled ? disabledStyle : styles[variant]) }}
    >
      {icon}
      {children}
    </button>
  );
}

function Field({ label, placeholder, search }: { label: string; placeholder: string; search?: boolean }) {
  const [focus, setFocus] = useState(false);
  return (
    <div>
      <label style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 500, color: TOKENS.ink, display: "block", marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        {search && <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: TOKENS.faint }} />}
        <input
          placeholder={placeholder}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            width: "100%",
            fontFamily: SANS,
            fontSize: "0.9375rem",
            color: TOKENS.body,
            background: TOKENS.card,
            border: `1px solid ${focus ? TOKENS.amethyst : TOKENS.clayBorder}`,
            borderRadius: 6,
            padding: search ? "9px 12px 9px 38px" : "9px 12px",
            outline: "none",
            boxShadow: focus ? `0 0 0 3px ${TOKENS.amethyst}4d` : "none",
            boxSizing: "border-box",
            transition: "border-color 140ms ease, box-shadow 140ms ease",
          }}
        />
      </div>
    </div>
  );
}

function Pill({ active, children }: { active?: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: SANS,
        fontSize: "0.8125rem",
        fontWeight: 500,
        borderRadius: 9999,
        padding: "4px 12px",
        background: active ? TOKENS.amethyst : TOKENS.clayMuted,
        color: active ? "#fff" : TOKENS.body,
      }}
    >
      {children}
    </span>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: SANS,
        fontSize: "0.8125rem",
        borderRadius: 9999,
        padding: "2px 10px",
        background: TOKENS.amethystSoft,
        color: TOKENS.amethystInk,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {children}
    </span>
  );
}

// --- Progress signature ---------------------------------------------------------

function OrganizationTrack({ value }: { value: number }) {
  return (
    <div style={{ height: 8, background: TOKENS.clayMuted, borderRadius: 9999, overflow: "hidden" }}>
      <div
        style={{
          height: "100%",
          width: `${value}%`,
          borderRadius: 9999,
          background: `linear-gradient(90deg, ${TOKENS.amethyst}, ${TOKENS.sage})`,
        }}
      />
    </div>
  );
}

// --- Page -----------------------------------------------------------------------

export default function DesignSystemV2Page() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: TOKENS.paper,
        fontFamily: SANS,
        color: TOKENS.body,
        // Scoped browser-surface theming for the preview
        // @ts-expect-error custom props
        "--sel-bg": TOKENS.amethystSoft,
      }}
    >
      <style>{`
        .ds2 ::selection { background: ${TOKENS.amethystSoft}; color: ${TOKENS.ink}; }
        .ds2 input { caret-color: ${TOKENS.amethyst}; }
        .ds2 .num { font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="ds2" style={{ maxWidth: 1024, margin: "0 auto", padding: 32, display: "flex", flexDirection: "column", gap: 48 }}>
        {/* Header */}
        <header>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: TOKENS.amethyst, display: "grid", placeItems: "center" }}>
              <HomeIcon size={18} color="#fff" />
            </div>
            <span style={{ fontFamily: DISPLAY, fontSize: "1.5rem", fontWeight: 600, color: TOKENS.amethystInk }}>Homethyst</span>
            <span style={{ fontFamily: SANS, fontSize: "0.8125rem", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: TOKENS.sageDeep, background: TOKENS.sageWash, padding: "3px 8px", borderRadius: 9999 }}>
              Warm Order · v2
            </span>
          </div>
          <h1 style={{ fontFamily: DISPLAY, fontSize: "2.5rem", fontWeight: 600, lineHeight: 1.05, letterSpacing: "-0.01em", color: TOKENS.ink, margin: 0 }}>
            The Homethyst Design System
          </h1>
          <p style={{ fontFamily: SANS, fontSize: "0.9375rem", lineHeight: 1.55, color: TOKENS.body, maxWidth: "60ch", margin: "12px 0 0" }}>
            A calm home operating system. Amethyst is the ink and the signal, warm paper is the ground,
            and sage is the visible payoff for order — restraint that reads as confidence, not as washed-out softness.
          </p>
        </header>

        {/* Colors */}
        <section>
          <SectionHeading kicker="Three roles that never trade places. Tints are designed tokens, not opacity on one hue.">Color</SectionHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {COLOR_GROUPS.map((group) => (
              <div key={group.title}>
                <h3 style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink, margin: "0 0 2px" }}>{group.title}</h3>
                <p style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, margin: "0 0 12px" }}>{group.note}</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                  {group.swatches.map((s) => (
                    <div key={s.name} style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${TOKENS.clayBorder}` }}>
                      <div style={{ background: s.hex, height: 72, display: "flex", alignItems: "flex-end", padding: 10 }}>
                        <code style={{ fontFamily: SANS, fontSize: "0.8125rem", color: s.text ?? "#fff", opacity: 0.9 }}>{s.hex}</code>
                      </div>
                      <div style={{ background: TOKENS.card, padding: "10px 12px" }}>
                        <div style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 600, color: TOKENS.ink }}>{s.name}</div>
                        <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, marginTop: 2, lineHeight: 1.4 }}>{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section>
          <SectionHeading kicker="Two calm sans voices: Hanken Grotesk (a soft humanist) for titles and the logo; Inter for everything functional.">Typography</SectionHeading>
          <Card style={{ padding: 28 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {TYPE_SCALE.map((t) => (
                <div key={t.label} style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "baseline", gap: 16, borderBottom: `1px solid ${TOKENS.clayBorder}`, paddingBottom: 18 }}>
                  <div
                    style={{
                      fontFamily: t.font,
                      fontSize: t.size,
                      fontWeight: t.weight,
                      lineHeight: t.lh,
                      letterSpacing: t.ls,
                      color: t.faint ? TOKENS.faint : TOKENS.ink,
                    }}
                  >
                    {t.sample}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: SANS, fontSize: "0.8125rem", fontWeight: 500, color: TOKENS.body }}>{t.label}</div>
                    <code style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>{t.meta}</code>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* Shape + Spacing */}
        <section>
          <SectionHeading kicker="A non-uniform, un-pillowy radius scale (max 14px) and a 4px spacing base with room to breathe.">Shape &amp; Spacing</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            <Card>
              <div style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 600, color: TOKENS.ink, marginBottom: 16 }}>Radii</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {RADII.map((r) => (
                  <div key={r.label} style={{ textAlign: "center" }}>
                    <div style={{ width: 60, height: 60, borderRadius: r.r, background: TOKENS.amethystSoft, border: `1.5px solid ${TOKENS.amethyst}66` }} />
                    <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.body, marginTop: 8 }}>{r.label}</div>
                    <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>{r.note}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <div style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 600, color: TOKENS.ink, marginBottom: 16 }}>Spacing</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {SPACING.map((s) => (
                  <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <code className="num" style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, width: 28 }}>{s.name}</code>
                    <div style={{ width: s.val, height: 14, background: TOKENS.sage, borderRadius: 3 }} />
                    <span className="num" style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>{s.val}px</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Buttons */}
        <section>
          <SectionHeading kicker="10px radius. Amethyst leads; sage-toned assistive buttons carry AI / assistive actions.">Buttons</SectionHeading>
          <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <Btn variant="primary" icon={<Plus size={16} />}>Add item</Btn>
              <Btn variant="secondary">Secondary</Btn>
              <Btn variant="ghost">Ghost</Btn>
              <Btn variant="destructive" icon={<X size={16} />}>Delete</Btn>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <Btn variant="assistive" icon={<Sparkles size={16} />}>Quick Reset</Btn>
              <Btn variant="assistive" icon={<RefreshCw size={16} />}>Visual Scan</Btn>
              <Pill active>All</Pill>
              <Pill>Kitchen</Pill>
              <Pill>Fragile</Pill>
            </div>
            <div style={{ borderTop: `1px solid ${TOKENS.clayBorder}`, paddingTop: 16 }}>
              <div style={{ fontFamily: SANS, fontSize: "0.8125rem", fontWeight: 500, color: TOKENS.faint, marginBottom: 8 }}>
                Disabled — a distinct inert treatment, not a faded fill
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <Btn variant="primary" disabled>Save changes</Btn>
                <span style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>
                  Add at least one item first. <span style={{ color: TOKENS.body }}>Disabled is never silent — it says why.</span>
                </span>
              </div>
            </div>
          </Card>
        </section>

        {/* Form */}
        <section>
          <SectionHeading kicker="White fields on paper, 6px radius, a visible amethyst focus ring, labels always shown.">Forms</SectionHeading>
          <Card style={{ maxWidth: 460, display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Item name" placeholder="e.g. Winter coat" />
            <Field label="Search" placeholder="Search items…" search />
            <div>
              <label style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 500, color: TOKENS.ink, display: "block", marginBottom: 6 }}>Tags</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 8, border: `1px solid ${TOKENS.clayBorder}`, borderRadius: 6, minHeight: 42 }}>
                <Chip><MapPin size={12} /> Kitchen <X size={12} /></Chip>
                <Chip>fragile <X size={12} /></Chip>
              </div>
            </div>
          </Card>
        </section>

        {/* Cards & patterns */}
        <section>
          <SectionHeading kicker="Flat by default — white on paper with warm hairlines. No resting shadows. Never nest a card in a card.">Cards &amp; Patterns</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            {/* Stat */}
            <Card style={{ textAlign: "center" }}>
              <div className="num" style={{ fontFamily: DISPLAY, fontSize: "2rem", fontWeight: 600, color: TOKENS.amethystInk }}>142</div>
              <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, marginTop: 2 }}>Items catalogued</div>
            </Card>

            {/* Item row */}
            <Card interactive>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: TOKENS.paper, border: `1px solid ${TOKENS.clayBorder}`, display: "grid", placeItems: "center" }}>
                  <Package size={18} color={TOKENS.amethyst} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink }}>Cast-iron skillet</span>
                    <span className="num" style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>×1</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <Chip><MapPin size={12} /> Kitchen</Chip>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick action */}
            <Card interactive>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: TOKENS.amethystSoft, display: "grid", placeItems: "center", marginBottom: 10 }}>
                <Plus size={20} color={TOKENS.amethyst} />
              </div>
              <div style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink }}>Add item</div>
              <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>Log something new</div>
            </Card>

            {/* AI action */}
            <Card interactive>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: TOKENS.sageWash, display: "grid", placeItems: "center", marginBottom: 10 }}>
                <Sparkles size={20} color={TOKENS.sageDeep} />
              </div>
              <div style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink }}>Quick Reset</div>
              <div style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>AI-assisted tidy</div>
            </Card>
          </div>

          {/* Progress signature */}
          <Card style={{ marginTop: 16, maxWidth: 460 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink }}>Home organization</span>
              <span className="num" style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 600, color: TOKENS.sageDeep }}>82%</span>
            </div>
            <OrganizationTrack value={82} />
            <p style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint, margin: "10px 0 0" }}>
              The signature: amethyst (work to do) resolves into visible sage (at rest) as order climbs.
            </p>
          </Card>
        </section>

        {/* States */}
        <section>
          <SectionHeading kicker="Empty, loading, and error earn the same craft budget as the happy path.">States</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            {/* Empty */}
            <div style={{ background: TOKENS.sageWash, border: `1px solid ${TOKENS.clayBorder}`, borderRadius: 14, padding: 28, textAlign: "center" }}>
              <div style={{ width: 44, height: 44, borderRadius: 9999, background: "#fff", display: "grid", placeItems: "center", margin: "0 auto 12px" }}>
                <Check size={20} color={TOKENS.sageDeep} />
              </div>
              <div style={{ fontFamily: DISPLAY, fontSize: "1.25rem", fontWeight: 600, color: TOKENS.ink }}>All caught up</div>
              <p style={{ fontFamily: SANS, fontSize: "0.9375rem", color: TOKENS.body, margin: "6px 0 14px" }}>This space is fully organized. Nothing needs you.</p>
              <Btn variant="secondary">Add an item</Btn>
            </div>

            {/* Loading */}
            <Card>
              <div style={{ fontFamily: SANS, fontSize: "0.8125rem", fontWeight: 500, color: TOKENS.faint, marginBottom: 12 }}>Loading (skeleton)</div>
              {[80, 60, 90].map((w, i) => (
                <div key={i} style={{ height: 14, width: `${w}%`, background: TOKENS.clayMuted, borderRadius: 6, marginBottom: 10 }} />
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: TOKENS.clayMuted }} />
                <div style={{ flex: 1, height: 44, borderRadius: 10, background: TOKENS.paperRaised, border: `1px solid ${TOKENS.clayBorder}` }} />
              </div>
            </Card>

            {/* Error */}
            <div style={{ background: TOKENS.errorWash, border: `1px solid ${TOKENS.error}55`, borderRadius: 14, padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <AlertTriangle size={18} color={TOKENS.error} />
                <span style={{ fontFamily: SANS, fontSize: "1.0625rem", fontWeight: 600, color: TOKENS.ink }}>Couldn't save</span>
              </div>
              <p style={{ fontFamily: SANS, fontSize: "0.9375rem", color: TOKENS.errorText, margin: "0 0 14px" }}>
                We lost the connection before your item saved. Your text is still here.
              </p>
              <Btn variant="primary">Try again</Btn>
            </div>
          </div>

          {/* Two-greens clarity: Success (active) vs Sage (assistive / at-rest) side by side */}
          <Card style={{ marginTop: 16 }}>
            <div style={{ fontFamily: SANS, fontSize: "0.8125rem", fontWeight: 500, color: TOKENS.faint, marginBottom: 12 }}>
              Two greens, two jobs — kept far enough apart to never be confused
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              {/* Success toast (vivid emerald) */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: TOKENS.successWash, border: `1px solid ${TOKENS.success}55`, borderRadius: 10, padding: "8px 14px" }}>
                <span style={{ width: 20, height: 20, borderRadius: 9999, background: TOKENS.success, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Check size={12} color="#fff" />
                </span>
                <span style={{ fontFamily: SANS, fontSize: "0.875rem", fontWeight: 500, color: TOKENS.successText }}>Item saved</span>
              </div>
              <span style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>Success — vivid, active</span>

              {/* Sage assistive button (muted) */}
              <Btn variant="assistive" icon={<Sparkles size={16} />}>Quick Reset</Btn>
              <span style={{ fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>Sage — muted, assistive / at-rest</span>
            </div>
          </Card>
        </section>

        <footer style={{ borderTop: `1px solid ${TOKENS.clayBorder}`, paddingTop: 24, fontFamily: SANS, fontSize: "0.8125rem", color: TOKENS.faint }}>
          Homethyst Design System — “Warm Order” · prototype at <code style={{ background: TOKENS.amethystSoft, color: TOKENS.amethystInk, padding: "2px 6px", borderRadius: 6 }}>/design-system-v2</code>
        </footer>
      </div>
    </div>
  );
}
